'use client'

import { Maximize, Minimize, Pause, Play, Volume1, Volume2, VolumeX } from 'lucide-react'
import { FC, useEffect, useRef, useState } from 'react'
import { formatPlaybackTime } from './formatPlaybackTime'

export type WideChapter = { time: number; title: string }

export type WideControlsBarProps = {
  isPlaying: boolean
  isMuted: boolean
  /** 音量（0〜1） */
  volume: number
  currentTime: number
  duration: number
  playbackRate: number
  isFullscreen: boolean
  /** 再生バーに打つ目印。ワイド型の目次の行（同じ動画の場面へ移るもの）から作る（ADR-0026） */
  chapters: WideChapter[]
  onPlayPause: () => void
  onToggleMute: () => void
  onVolumeChange: (volume: number) => void
  onSeek: (time: number) => void
  onPlaybackRateChange: (rate: number) => void
  onToggleFullscreen: () => void
  /**
   * true の間は自動で隠さず、選択肢の幕より前面に出す（選択肢表示中・一時停止中）。
   * false の間は、動画上でマウスを動かす・触ると出て、しばらく操作が無いと隠れる。
   */
  pinned?: boolean
  /** バーが出た・隠れたときの通知（動画に重ねた目次を一緒に出し隠しするのに使う） */
  onVisibleChange?: (visible: boolean) => void
}

/** 選択肢をこのバーの上に持ち上げるための、下端からの余白（バーの操作部分の高さ） */
export const WIDE_CONTROLS_CLEARANCE = 52

/** 操作が無くなってからバーを隠すまでの時間 */
const AUTO_HIDE_DELAY_MS = 3000

const PLAYBACK_RATES = [1, 1.25, 1.5, 2]

/**
 * スマホか（指で操作し、画面の短い辺が600px未満）。スマホでは全画面ボタンと音量バーを出さない。
 * iPhone は枠ごとの全画面に対応しておらず、全画面にすると端末標準の動画画面に切り替わって
 * インタラクティブボタン・選択肢が出なくなるため。音量もページから変えられず、本体のボタンで調整するため
 */
const isPhone = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia('(hover: none) and (pointer: coarse)').matches &&
  Math.min(window.screen.width, window.screen.height) < 600

const iconButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '34px',
  height: '34px',
  border: 'none',
  background: 'transparent',
  color: '#fff',
  cursor: 'pointer',
  flexShrink: 0,
}

/**
 * ワイド型のコントロールバー。動画の下部に重ねる。
 *
 * ネイティブの `controls` は使わない（目次の目印のドットを打てないため）。
 * シークバーは既存 `ProgressBar` と違い**シーク可能**で、目次の行（同じ動画の場面へ移るもの）があれば
 * その位置にドットを出し、押すとその場面へ移る（ADR-0026。一覧は左の目次が担う）。
 */
const WideControlsBar: FC<WideControlsBarProps> = ({
  isPlaying,
  isMuted,
  volume,
  currentTime,
  duration,
  playbackRate,
  isFullscreen,
  chapters,
  onPlayPause,
  onToggleMute,
  onVolumeChange,
  onSeek,
  onPlaybackRateChange,
  onToggleFullscreen,
  pinned = false,
  onVisibleChange,
}) => {
  const [hoveredChapter, setHoveredChapter] = useState<WideChapter | null>(null)
  const [isCompact, setIsCompact] = useState(false)
  const [isPhoneDevice, setIsPhoneDevice] = useState(false)
  // 音量バーはスピーカーにマウスを乗せている間（またはバーにフォーカスがある間）だけ横に伸ばす
  const [isVolumeOpen, setIsVolumeOpen] = useState(false)
  const controlsRef = useRef<HTMLDivElement>(null)
  const [isActive, setIsActive] = useState(true)
  const isPointerOverBarRef = useRef(false)

  // 動画エリア（親要素）での操作を見て、バーを出す／しばらく操作が無ければ隠す。
  // バーの上にマウスがある間・バー内にフォーカスがある間は隠さない。
  useEffect(() => {
    const bar = controlsRef.current
    const area = bar?.parentElement
    if (!bar || !area) return

    let timer: ReturnType<typeof setTimeout> | undefined
    const scheduleHide = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        const root = bar.getRootNode() as Document | ShadowRoot
        const hasFocusInside = root.activeElement instanceof Node && bar.contains(root.activeElement)
        if (isPointerOverBarRef.current || hasFocusInside) {
          scheduleHide()
          return
        }
        setIsActive(false)
      }, AUTO_HIDE_DELAY_MS)
    }
    const wake = () => {
      setIsActive(true)
      scheduleHide()
    }
    const hideNow = () => {
      clearTimeout(timer)
      if (!isPointerOverBarRef.current) setIsActive(false)
    }

    scheduleHide()
    area.addEventListener('pointermove', wake)
    area.addEventListener('pointerdown', wake)
    area.addEventListener('focusin', wake)
    area.addEventListener('mouseleave', hideNow)
    return () => {
      clearTimeout(timer)
      area.removeEventListener('pointermove', wake)
      area.removeEventListener('pointerdown', wake)
      area.removeEventListener('focusin', wake)
      area.removeEventListener('mouseleave', hideNow)
    }
  }, [])

  const isVisible = pinned || isActive
  useEffect(() => {
    onVisibleChange?.(isVisible)
  }, [isVisible, onVisibleChange])

  useEffect(() => {
    setIsPhoneDevice(isPhone())
  }, [])

  useEffect(() => {
    const element = controlsRef.current
    if (!element) return

    const sync = () => setIsCompact(element.getBoundingClientRect().width < 480)
    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const safeDuration = duration > 0 ? duration : 0
  const progress = safeDuration > 0 ? Math.min(currentTime / safeDuration, 1) : 0
  // 動画長を超える目印は出さない（動画を差し替えて短くなった場合の保険）
  const visibleChapters = chapters.filter((c) => safeDuration > 0 && c.time >= 0 && c.time < safeDuration)

  const handleRateClick = () => {
    const next = PLAYBACK_RATES[(PLAYBACK_RATES.indexOf(playbackRate) + 1) % PLAYBACK_RATES.length]
    onPlaybackRateChange(next)
  }

  return (
    <div
      ref={controlsRef}
      data-testid="wide-controls-bar"
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        // 選択肢表示中は幕（zIndex 30）より前に出して押せるようにする
        zIndex: pinned ? 31 : 20,
        padding: isCompact ? '15px 8px 6px' : '20px 14px 9px',
        background: 'linear-gradient(to top, rgba(8,18,38,0.92) 0%, rgba(8,18,38,0.68) 48%, rgba(8,18,38,0) 100%)',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 200ms ease',
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
      onPointerEnter={() => {
        isPointerOverBarRef.current = true
      }}
      onPointerLeave={() => {
        isPointerOverBarRef.current = false
      }}
    >
      {/* シークバー（ドットマーカー付き） */}
      <div style={{ position: 'relative', padding: isCompact ? '5px 3px' : '6px 4px' }}>
        {hoveredChapter && (
          <div
            style={{
              position: 'absolute',
              bottom: '19px',
              left: `${(hoveredChapter.time / safeDuration) * 100}%`,
              transform: 'translateX(-50%)',
              padding: '4px 8px',
              borderRadius: '6px',
              background: 'rgba(17,24,39,0.95)',
              color: '#fff',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            {hoveredChapter.title}
          </div>
        )}

        <div
          style={{
            position: 'relative',
            height: '3px',
            borderRadius: '999px',
            background: 'rgba(255,255,255,0.34)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              width: `${progress * 100}%`,
              borderRadius: '999px',
              background: 'var(--video-brand-color, var(--video-accent-color, #60a5fa))',
            }}
          />
          {safeDuration > 0 && progress > 0 && (
            <span
              style={{
                position: 'absolute',
                top: '50%',
                left: `${progress * 100}%`,
                width: '7px',
                height: '7px',
                borderRadius: '50%',
                background: '#fff',
                boxShadow: '0 1px 5px rgba(0,0,0,0.32)',
                transform: 'translate(-50%, -50%)',
              }}
            />
          )}
          {visibleChapters.map((chapter) => (
            // 透明シークバー（input[type=range]）より前面（zIndex）に置かないとクリックも
            // ホバーも range に横取りされ、目印の場面へ正確に飛べない。当たり判定は
            // 見た目のドット（8px）より広い透明ボタンで確保する。
            <button
              key={`dot-${chapter.time}`}
              type="button"
              data-testid="wide-chapter-dot"
              aria-label={`目次: ${chapter.title}`}
              onMouseEnter={() => setHoveredChapter(chapter)}
              onMouseLeave={() => setHoveredChapter(null)}
              onClick={() => onSeek(chapter.time)}
              style={{
                position: 'absolute',
                top: '50%',
                left: `${(chapter.time / safeDuration) * 100}%`,
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '20px',
                height: '20px',
                padding: 0,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                zIndex: 1,
              }}
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.78)',
                  background: 'var(--video-brand-color, var(--video-accent-color, #60a5fa))',
                }}
              />
            </button>
          ))}
        </div>

        {/* 実際のシーク操作は range で受ける（見た目は上のバー） */}
        <input
          type="range"
          min={0}
          max={safeDuration || 0}
          step={0.1}
          value={Math.min(currentTime, safeDuration)}
          onChange={(e) => onSeek(Number(e.target.value))}
          aria-label="再生位置"
          style={{
            position: 'absolute',
            left: '4px',
            right: '4px',
            top: '1px',
            width: 'calc(100% - 8px)',
            height: '12px',
            margin: 0,
            opacity: 0,
            cursor: 'pointer',
          }}
        />
      </div>

      <div style={{ display: 'flex', minHeight: '34px', alignItems: 'center', gap: isCompact ? '1px' : '3px' }}>
        <button
          type="button"
          onClick={onPlayPause}
          aria-label={isPlaying ? '一時停止' : '再生'}
          style={iconButtonStyle}
        >
          {isPlaying ? <Pause size={isCompact ? 18 : 20} /> : <Play size={isCompact ? 18 : 20} />}
        </button>
        {/* スピーカーは押すとミュート／音声オンの切り替え。PC ではマウスを乗せると右に音量バーが伸びる（YouTube と同じ） */}
        <div
          onMouseEnter={() => setIsVolumeOpen(true)}
          onMouseLeave={() => setIsVolumeOpen(false)}
          onFocus={() => setIsVolumeOpen(true)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsVolumeOpen(false)
          }}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <button
            type="button"
            onClick={onToggleMute}
            aria-label={isMuted ? 'ミュート解除' : 'ミュート'}
            style={iconButtonStyle}
          >
            {isMuted || volume === 0 ? (
              <VolumeX size={isCompact ? 18 : 20} />
            ) : volume < 0.5 ? (
              <Volume1 size={isCompact ? 18 : 20} />
            ) : (
              <Volume2 size={isCompact ? 18 : 20} />
            )}
          </button>
          {!isPhoneDevice && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                width: isVolumeOpen ? (isCompact ? '64px' : '84px') : 0,
                overflow: 'hidden',
                transition: 'width 0.2s ease',
              }}
            >
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={isMuted ? 0 : volume}
                onChange={(event) => onVolumeChange(Number(event.target.value))}
                aria-label="音量"
                style={{
                  width: isCompact ? '52px' : '72px',
                  margin: '0 10px 0 2px',
                  accentColor: '#fff',
                  cursor: 'pointer',
                }}
              />
            </div>
          )}
        </div>
        <span
          style={{
            color: 'rgba(255,255,255,0.9)',
            fontSize: isCompact ? '11px' : '13px',
            fontWeight: 650,
            fontVariantNumeric: 'tabular-nums',
            padding: isCompact ? '0 3px' : '0 6px',
          }}
        >
          {formatPlaybackTime(currentTime)} {!isCompact && `/ ${formatPlaybackTime(safeDuration)}`}
        </span>

        <span style={{ flex: 1 }} />

        <button
          type="button"
          onClick={handleRateClick}
          aria-label="再生速度"
          style={{
            ...iconButtonStyle,
            width: 'auto',
            padding: isCompact ? '0 5px' : '0 8px',
            fontSize: isCompact ? '11px' : '13px',
            fontWeight: 750,
          }}
        >
          {playbackRate}x
        </button>
        {!isPhoneDevice && (
          <button type="button" onClick={onToggleFullscreen} aria-label="全画面" style={iconButtonStyle}>
            {isFullscreen ? <Minimize size={isCompact ? 18 : 20} /> : <Maximize size={isCompact ? 18 : 20} />}
          </button>
        )}
      </div>
    </div>
  )
}

export default WideControlsBar
